import { useState, useEffect, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  Loader2,
  BadgeIndianRupee,
  ShoppingBag,
  Container
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dashboardService } from "@/services/api";
import type { DashboardData } from "@/types/api";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { useToast } from "@/hooks/use-toast";
import { AuthContext } from "@/context/AuthContext";

const Dashboard = () => {
  const { toast } = useToast();
  const { currentUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    inventorySummary: {
      totalStocks: 0,
      yesterdaySales: 0,
      outOfStock: 0,
      totalProfit: 0
    },
    lowStocks: [],
    slowMoving: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError("");

      try {
        const summary = await dashboardService.getSummary();
        const lowStocks = await dashboardService.getLowStocks();
        const slowMoving = await dashboardService.getSlowMoving();

        setDashboardData({
          inventorySummary: summary.data,
          lowStocks: lowStocks.data,
          slowMoving: slowMoving.data
        });

        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
        setIsLoading(false);
      }
    };

      fetchDashboardData();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          {currentUser?.role === 'Admin' && (
            <Badge variant="outline" className="bg-primary/10 text-primary">
              Admin Access
            </Badge>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="text-center text-red-500">
                <AlertTriangle size={40} className="mx-auto mb-4" />
                <p>{error}</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <p className="text-sm text-muted-foreground">Total</p>
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <BadgeIndianRupee size={20} className="text-blue-500" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold">{dashboardData.inventorySummary.totalProfit}</h3>
                  <p className="text-sm text-blue-500 flex items-center mt-2">
                    Year-to-Date (YTD) Profit
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <p className="text-sm text-muted-foreground">Sales</p>
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <ShoppingBag size={20} className="text-amber-500" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-amber-500">{dashboardData.inventorySummary.yesterdaySales}</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Yesterday Total Sales
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <p className="text-sm text-muted-foreground">Total Stocks</p>
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Container size={20} className="text-primary" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold">{dashboardData.inventorySummary.totalStocks}</h3>
                  <p className="text-sm text-green-500 flex items-center mt-2">
                    In Store
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <p className="text-sm text-muted-foreground">Out of Stock</p>
                    <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle size={20} className="text-red-500" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-red-500">{dashboardData.inventorySummary.outOfStock}</h3>
                  <p className="text-sm text-red-500 mt-2">
                    Requires immediate action
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Main dashboard content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Activity */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle>Slow Moving Products</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Sold Last 3 Months</TableHead>
                          <TableHead>Stock</TableHead>
                          <TableHead>Difference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoading ? (
                          <TableRow>
                            <TableCell colSpan={14} className="text-center py-10">
                              <div className="flex justify-center">
                                <Loader2 size={30} className="animate-spin text-primary" />
                              </div>
                              <p className="mt-2 text-muted-foreground">Loading reports...</p>
                            </TableCell>
                          </TableRow>
                        ) : dashboardData.slowMoving.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">
                              No data found
                            </TableCell>
                          </TableRow>
                        ) : (
                          dashboardData.slowMoving.map((stock) => (
                            <TableRow key={stock.id}>
                              <TableCell className="font-medium">{stock.product}</TableCell>
                              <TableCell className="font-medium">{stock.soldLast3Months}</TableCell>
                              <TableCell>{stock.stock}</TableCell>
                              <TableCell>{stock.difference}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              {/* Popular Items */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle>Low Stocks</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {dashboardData.lowStocks.map((item) => (
                      <div
                        key={item.id}
                        className="bg-accent/50 p-3 rounded-lg hover:bg-accent transition-colors"
                      >
                        <div className="flex justify-between mb-1">
                          <p className="font-medium">{item.product}</p>
                          <p className="font-semibold text-primary">{item.stock}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;